const modulesApiName = "ekocropApi";
const apiObject = {};
export const featureChangeEventName = "ekocrop-feature-change";
const enabledFeaturesStorageKey = "ekocropEnabledFeatures";

// Store enabled features
const enabledFeatures = new Set<string>();
let enabledFeaturesLoaded = false;

// Define proper type for global context
interface EkocropApi {
  [key: string]: unknown;
  enableFeature?: (featureId: string) => void;
  disableFeature?: (featureId: string) => void;
  toggleFeature?: (featureId: string, value: boolean) => void;
}

interface GlobalContext extends Window {
  [key: string]: unknown;
  [modulesApiName]?: EkocropApi;
}

const getEnabledFeaturesStorage = (): Storage | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch (e) {
    console.warn("Feature flags storage is unavailable", e);
    return undefined;
  }
};

const loadEnabledFeatures = () => {
  if (enabledFeaturesLoaded) {
    return;
  }

  enabledFeaturesLoaded = true;

  const storage = getEnabledFeaturesStorage();

  if (!storage) {
    return;
  }

  try {
    const persistedFeatures = JSON.parse(
      storage.getItem(enabledFeaturesStorageKey) || "[]"
    );

    if (Array.isArray(persistedFeatures)) {
      persistedFeatures.forEach((featureId) => {
        if (typeof featureId === "string") {
          enabledFeatures.add(featureId);
        }
      });
    }
  } catch (e) {
    console.warn("Could not restore feature flags", e);
  }
};

const persistEnabledFeatures = () => {
  const storage = getEnabledFeaturesStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(enabledFeaturesStorageKey, JSON.stringify([...enabledFeatures]));
  } catch (e) {
    console.warn("Could not persist feature flags", e);
  }
};

// Initialize feature system
const initializeFeatureSystem = () => {
  loadEnabledFeatures();

  const globalContext = window as unknown as GlobalContext;

  if (!globalContext[modulesApiName]) {
    globalContext[modulesApiName] = apiObject;
  }

  const notifyFeatureChanged = (featureId: string) => {
    window.dispatchEvent(new CustomEvent(featureChangeEventName, { detail: featureId }));
  };

  // Add enableFeature method to ekocropApi
  globalContext[modulesApiName].enableFeature = (featureId: string) => {
    enabledFeatures.add(featureId);
    persistEnabledFeatures();
    // Show all elements with this feature id
    const elements = document.querySelectorAll(`[hidden-by-id="${featureId}"]`);
    elements.forEach((el: Element) => {
      (el as HTMLElement).style.display = "";
    });
    console.log(`Feature "${featureId}" enabled`);
    notifyFeatureChanged(featureId);
  };

  globalContext[modulesApiName].disableFeature = (featureId: string) => {
    enabledFeatures.delete(featureId);
    persistEnabledFeatures();
    hideFeatureElements();
    console.log(`Feature "${featureId}" disabled`);
    notifyFeatureChanged(featureId);
  };

  globalContext[modulesApiName].toggleFeature = function (
    featureId: string,
    value: boolean
  ) {
    if (value) {
      this?.enableFeature?.(featureId);
    } else {
      this?.disableFeature?.(featureId);
    }
  };

  // Hide elements with hidden-by-id attribute that aren't enabled
  const hideFeatureElements = () => {
    const elements = document.querySelectorAll("[hidden-by-id]");
    elements.forEach((el: Element) => {
      const featureId = el.getAttribute("hidden-by-id");
      if (featureId && !enabledFeatures.has(featureId)) {
        (el as HTMLElement).style.display = "none";
      }
    });
  };

  // Run on DOM changes (for re-renders)
  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver(hideFeatureElements);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Hide elements initially
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hideFeatureElements);
  } else {
    hideFeatureElements();
  }
};

export function isFeatureEnabled(featureId: string): boolean {
  loadEnabledFeatures();

  return enabledFeatures.has(featureId);
}

export function useModuleApi<T>(name: string, moduleApi: T): T {
  const globalContext = (window || {}) as unknown as GlobalContext;

  if (!Object.prototype.hasOwnProperty.call(globalContext, modulesApiName)) {
    globalContext[modulesApiName] = apiObject;
    console.log(
      `Ekocrop console API is available by key "${modulesApiName}" in global context (window)`
    );
  }

  if (typeof globalContext[modulesApiName] !== "object") {
    throw new Error("Api object could not be extended");
  }

  if (!Object.prototype.hasOwnProperty.call(apiObject, name)) {
    apiObject[name] = moduleApi;
  }

  // Initialize the feature system
  initializeFeatureSystem();

  return moduleApi;
}
